import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface GoogleSignInButtonProps {
  onCredential: (idToken: string) => void;
  text?: 'signin_with' | 'signup_with' | 'continue_with';
  disabled?: boolean;
}

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

/**
 * Renders the official Google Identity Services button.
 * Returns the credential (Google ID JWT) via `onCredential`.
 */
export default function GoogleSignInButton({
  onCredential,
  text = 'continue_with',
  disabled = false,
}: GoogleSignInButtonProps) {
  const { i18n, t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);
  const callbackRef = useRef(onCredential);
  callbackRef.current = onCredential;

  // Wait for the GIS script to load (added in index.html as async)
  useEffect(() => {
    if (!CLIENT_ID) return;
    if (window.google?.accounts?.id) {
      setReady(true);
      return;
    }
    const interval = window.setInterval(() => {
      if (window.google?.accounts?.id) {
        setReady(true);
        window.clearInterval(interval);
      }
    }, 100);
    const timeout = window.setTimeout(() => window.clearInterval(interval), 8000);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (!ready || !containerRef.current || !CLIENT_ID) return;

    window.google!.accounts.id.initialize({
      client_id: CLIENT_ID,
      callback: (response) => {
        if (response?.credential) {
          callbackRef.current(response.credential);
        }
      },
      ux_mode: 'popup',
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    containerRef.current.innerHTML = '';
    window.google!.accounts.id.renderButton(containerRef.current, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text,
      shape: 'rectangular',
      logo_alignment: 'left',
      width: containerRef.current.clientWidth || 320,
      locale: i18n.language,
    });
  }, [ready, text, i18n.language]);

  if (!CLIENT_ID) {
    // Fail silently in dev if not configured; shows a placeholder so the UI doesn't break.
    return (
      <div className="text-xs text-gray-400 text-center py-2">
        {t('auth.googleNotConfigured', 'Google sign-in not configured')}
      </div>
    );
  }

  return (
    <div className={disabled ? 'pointer-events-none opacity-60' : ''}>
      <div ref={containerRef} className="flex justify-center min-h-[44px]" />
    </div>
  );
}
